import { useState, useCallback, useRef, useEffect } from 'react';
import { SocketManager } from '@mx/socket/dist/socket';
import { TENANT, DOMAIN, AGGREGATE, EVENT } from '@mx/socket/dist/constants';
import { config } from '@/config';
import { AuthorizationData, SocketAuthorizeTokenPayload } from '../types/socket.types';
import { MessageParser } from '../utils/MessageParser';
import { SOCKET_CONFIG } from '../constants/socket.constants';

interface UseSocketManagerReturn {
  isConnected: boolean;
  isConnecting: boolean;
  connect(sessionId: string): Promise<void>;
  disconnect(): void;
}

interface SocketCallbacks {
  onError?(error: string): void;
  onConnectionStatusChange?(isConnected: boolean): void;
  onAuthorization?(authData: AuthorizationData): void;
  onDeniedAuthorization?(authData: Partial<AuthorizationData>): void;
  onAccessToken?(accessToken: string, sessionId: string): void;
}

export const useSocketManager = (callbacks: SocketCallbacks = {}): UseSocketManagerReturn => {
  const [socketManager, setSocketManager] = useState<SocketManager | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentChannel, setCurrentChannel] = useState<string | null>(null);

  const sessionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const callbacksRef = useRef<SocketCallbacks>(callbacks);

  useEffect(() => {
    callbacksRef.current = callbacks;
  }, [callbacks]);

  const getIdToken = useCallback(async () => {
    const token = new Date().getTime().toString();
    return token;
  }, []);

  const clearSessionTimeout = useCallback(() => {
    if (sessionTimeoutRef.current) {
      clearTimeout(sessionTimeoutRef.current);
      sessionTimeoutRef.current = null;
    }
  }, []);

  const handleConnectionSuccess = useCallback(() => {
    clearSessionTimeout();
    setIsConnected(true);
    setIsConnecting(false);
    callbacksRef.current.onConnectionStatusChange?.(true);
  }, [clearSessionTimeout]);

  const handleConnectionFailure = useCallback(
    (error: string) => {
      clearSessionTimeout();
      setIsConnecting(false);
      setIsConnected(false);
      callbacksRef.current.onError?.(error);
      callbacksRef.current.onConnectionStatusChange?.(false);
    },
    [clearSessionTimeout],
  );

  const handleSessionEnd = useCallback(() => {
    setIsConnected(false);
    callbacksRef.current.onConnectionStatusChange?.(false);
  }, []);

  const handleSocketMessage = useCallback(
    (data: SocketAuthorizeTokenPayload, sessionId: string) => {
      console.log('data', data);
      const actualData = MessageParser.processSocketData(data);

      if (!actualData) {
        return;
      }

      // Handle session acceptance
      if (MessageParser.isSessionAcceptedEvent(actualData)) {
        handleConnectionSuccess();
        return;
      }

      // Handle denied authorization events
      if (MessageParser.isDeniedAuthorizationEvent(actualData)) {
        // Deny login in Compass when access through Denise
        callbacksRef.current.onDeniedAuthorization?.(actualData);
        return;
      }

      // Handle authorization events (access token received)
      if (MessageParser.isAuthorizationEvent(actualData)) {
        const authData = MessageParser.extractAuthorizationData(actualData);
        if (authData) {
          // Approve login in Compass when access through Denise
          callbacksRef.current.onAuthorization?.(authData);
          // Use the authData sessionId or fall back to the provided sessionId
          callbacksRef.current.onAccessToken?.(
            authData.accessToken,
            authData.sessionId || sessionId,
          );
          handleConnectionSuccess();
        }
        return;
      }

      // Handle session end
      if (MessageParser.isSessionEndEvent(actualData)) {
        handleSessionEnd();
        return;
      }
    },
    [handleConnectionSuccess, handleSessionEnd],
  );

  const setupConnectionTimeout = useCallback(
    (manager: SocketManager, channel: string) => {
      sessionTimeoutRef.current = setTimeout(() => {
        handleConnectionFailure('Connection timeout: Session not accepted');

        manager.removeSocketInstance(channel);
        setSocketManager(null);
        setCurrentChannel(null);
      }, SOCKET_CONFIG.CONNECTION_TIMEOUT);
    },
    [handleConnectionFailure],
  );

  const connect = useCallback(
    async (sessionId: string) => {
      if (isConnecting || (socketManager && isConnected)) {
        return;
      }

      setIsConnecting(true);

      try {
        const socketUrl = config.socketService.url;
        const manager = new SocketManager(getIdToken, socketUrl);

        const channel = manager.generateChannel({
          tenant: TENANT.XSCHOOL,
          domain: DOMAIN.OPS,
          aggregate: AGGREGATE.PORTAL,
        });

        setCurrentChannel(channel);
        setSocketManager(manager);

        await manager.addSocketInstance(channel);

        const eventName = `${EVENT.SOCKET_AUTHORIZATION_PORTAL_CHANNEL}_${sessionId}` as EVENT;
        manager.subscribe({
          channel,
          events: [eventName],
          callback: (data) => {
            handleSocketMessage(data, sessionId);
          },
        });

        // setupConnectionTimeout(manager, channel);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to connect';
        handleConnectionFailure(errorMessage);
      }
    },
    [
      isConnecting,
      socketManager,
      isConnected,
      getIdToken,
      handleSocketMessage,
      setupConnectionTimeout,
      handleConnectionFailure,
    ],
  );

  const disconnect = useCallback(() => {
    clearSessionTimeout();

    if (socketManager && currentChannel) {
      socketManager.removeSocketInstance(currentChannel);
    }

    setSocketManager(null);
    setCurrentChannel(null);
    setIsConnected(false);
    setIsConnecting(false);
    callbacksRef.current.onConnectionStatusChange?.(false);
  }, [socketManager, currentChannel, clearSessionTimeout]);

  useEffect(() => {
    const currentSocketManager = socketManager;
    const currentChannelValue = currentChannel;

    return () => {
      clearSessionTimeout();
      if (currentSocketManager && currentChannelValue) {
        currentSocketManager.removeSocketInstance(currentChannelValue);
      }
    };
  }, [socketManager, currentChannel, clearSessionTimeout]);

  return {
    isConnected,
    isConnecting,
    connect,
    disconnect,
  };
};
